import { useActiveSection } from '../../hooks';

const STEPS = [
  {
    title: 'Scope',
    body: 'We map the workflow, the data, and the outcome you need. You get a concrete plan and a fixed scope — before any commitment.',
    detail: 'Output: a written plan, a timeline, and a number.',
  },
  {
    title: 'Build',
    body: 'Short cycles with working software at every step. You see progress as it happens, not at the end.',
    detail: 'Output: something you can click, every cycle.',
  },
  {
    title: 'Deploy',
    body: 'We ship to production on your cloud — AWS, DigitalOcean, Railway, Hetzner, or PythonAnywhere — with CI/CD and monitoring in place.',
    detail: 'Output: live, monitored, and yours.',
  },
  {
    title: 'Run',
    body: 'We stay on after launch: support, iteration, and the next module when you need it.',
    detail: 'Output: a system that keeps earning its place.',
  },
] as const;

export function Process(): React.JSX.Element {
  const { refs, active } = useActiveSection(STEPS.length);

  return (
    <section id="process" className="process">
      <div className="container">
        <div className="process-layout">
          {/* The aside stretches to the full row height so the inner
              sticky block has room to travel alongside the panels. */}
          <div className="process-aside">
            <div className="process-sticky section-head-anim sh-float reveal">
            <h2 className="h2">
              Four steps, <span className="si accent-text">no surprises</span>.
            </h2>
            <p className="lede">
              The same sequence on every engagement, whether it's an agent on one number
              or an ERP across a whole company.
            </p>
            <ol className="process-rail" aria-label="Process steps">
              {STEPS.map((step, i) => (
                <li key={step.title} className={i === active ? 'current' : ''}>
                  <span className="rail-num">{String(i + 1).padStart(2, '0')}</span>
                  <span className="rail-title">{step.title}</span>
                </li>
                ))}
              </ol>
            </div>
          </div>

          <div className="process-steps">
            {STEPS.map((step, i) => (
              <article
                key={step.title}
                className={`process-panel ${i === active ? 'current' : ''}`}
                ref={(el) => {
                  refs.current[i] = el;
                }}
              >
                <span className="panel-num">{String(i + 1).padStart(2, '0')}</span>
                <h3 className="h3">{step.title}</h3>
                <p>{step.body}</p>
                <p className="panel-detail">{step.detail}</p>
              </article>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
